package top.wcpe.allincore.dsl.ir.model.statemachine

import top.wcpe.allincore.dsl.ir.model.internal.ReadOnlyList
import top.wcpe.allincore.dsl.ir.model.internal.ReadOnlyOrderedMap

/**
 * UI 状态机 IR（M10）。
 *
 * 描述一个被多个元素实例引用的命名状态机：包含初始状态、命名状态集合与状态间的迁移规则。
 * 运行时由 `StateMachineInstance` 持有 `currentState` 并按 [UiSmTransition]
 * 调度属性过渡。
 *
 * @see UiSmState
 * @see UiSmTransition
 */
class UiStateMachine(
    // val 属性生成 getId()/getInitialState()/getStates()/getTransitions()，与原 Java getter 名一致。
    // states/transitions 在 UiIr.getStateMachines() 内唯一（UiIr 尚在 jvmMain，故此处仅文字描述不做链接）。
    /** 状态机 id。 */
    val id: String,
    /** 初始状态名，必须存在于 [states] 中（编译期校验，IR 层不再强校）。 */
    val initialState: String,
    states: Map<String, UiSmState>,
    transitions: List<UiSmTransition>,
) {

    /** 状态名 → 状态定义；保序、不可变。 */
    val states: Map<String, UiSmState>

    /** 迁移规则，保序、不可变；查找规则由运行时实现（精确优先、any 兜底）。 */
    val transitions: List<UiSmTransition>

    init {
        // 防御式保序拷贝，并校验键值非空。
        val copiedStates = LinkedHashMap<String, UiSmState>(states.size)
        for ((key, value) in states) {
            requireNotNull(key) { "state key" }
            requireNotNull(value) { "state value" }
            copiedStates[key] = value
        }
        this.states = ReadOnlyOrderedMap(copiedStates)

        val copiedTransitions = ArrayList<UiSmTransition>(transitions.size)
        for (tr in transitions) {
            copiedTransitions.add(requireNotNull(tr) { "transition" })
        }
        this.transitions = ReadOnlyList(copiedTransitions)
    }

    override fun equals(other: Any?): Boolean {
        if (this === other) return true
        if (other !is UiStateMachine) return false
        return id == other.id &&
            initialState == other.initialState &&
            states == other.states &&
            transitions == other.transitions
    }

    // 逐位复刻 Objects.hash(id, initialState, states, transitions)：种子 1、31 倍累乘。
    override fun hashCode(): Int {
        var result = 1
        result = 31 * result + id.hashCode()
        result = 31 * result + initialState.hashCode()
        result = 31 * result + states.hashCode()
        result = 31 * result + transitions.hashCode()
        return result
    }

    override fun toString(): String =
        "UiStateMachine{" + id +
            ",initial=" + initialState +
            ",states=" + states.keys +
            ",transitions=" + transitions.size + "}"
}
